import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';

import { CurrentProjectService, CustomProjectConfiguration } from './current-project.service';
import { WorkPackagesService } from './work-package.service';
import { WorkPackageSortViewEnum } from '../pages/work-package-list-page/work-package-sort-view.enum';
import { Section } from '../utils';
import { SidebarItem } from '../widgets/sidebar-item/sidebar-item.component';
import { TreeUIFacade } from '../widgets/wp-tree-view/tree-core/tree-ui-facade';
import { WorkPackageTreeUiFacade } from '../widgets/wp-tree-view/wp-tree-ui-facade';
import { WorkPackageTreeNode } from '../widgets/wp-tree-view/wp-tree-view.component';
import { ProjectDto, WorkPackagePriorityDto, BaseWorkPackageDto } from '@api/backend-connector/models';

@Injectable({ providedIn: 'root' })
export class DefaultCustomProjectConfiguration implements CustomProjectConfiguration {
  private readonly workPackageService = inject(WorkPackagesService);
  private readonly currentProjectService = inject(CurrentProjectService);

  constructor() {
    this.currentProjectService.registerCustomConfiguration(this);
  }

  getPriority(): number {
    this;
    return 1000;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async canHandle(projectId: string): Promise<boolean> {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async allowHierarchy(projectId: string): Promise<boolean> {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async ganttEditable(projectId: string): Promise<boolean> {
    return true;
  }

  async getTreeUIFacade(projectId: string): Promise<TreeUIFacade<WorkPackageTreeNode>> {
    // autoSaveMoves: persist drag-and-drop moves through the API. Without it
    // the reorder is applied in memory only and gets lost on reload (there is
    // no other flow saving the mutable tree).
    return new WorkPackageTreeUiFacade(projectId, this.workPackageService, true);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async tasksInitialView(projectId: string): Promise<Params | null | undefined> {
    return {
      sort: WorkPackageSortViewEnum.Newest,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async openCreateWorkPackageDialog(
    dialog: DialogService,
    project: ProjectDto,
    priorities: WorkPackagePriorityDto[],
    parent?: BaseWorkPackageDto,
  ): Promise<BaseWorkPackageDto | undefined> {
    const { WorkPackageCreateDialogComponent } = await import(
      '../pages/work-package-create-dialog/work-package-create-dialog.component'
    );
    return WorkPackageCreateDialogComponent.open(dialog, project, priorities, parent);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async genericWorkPackagesFilters(projectId: string): Promise<SidebarItem[]> {
    return [
      {
        label: 'i18n.WorkPackageListPage.View.assignedToMe',
        id: 'assigned-to-me',
        icon: 'person-pin',
      },
      {
        label: 'i18n.WorkPackageListPage.View.createdByMe',
        id: 'created-by-me',
        icon: 'person-add',
      },
      {
        label: 'i18n.WorkPackageListPage.Filter.expired',
        id: 'expired',
        icon: 'warning',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, class-methods-use-this
  async workPackagesSortOptions(projectId: string): Promise<Section[]> {
    return [
      {
        label: 'i18n.WorkPackageListPage.View.Sort.byNewest',
        id: WorkPackageSortViewEnum.Newest,
        icon: 'sort-down',
      },
      {
        label: 'i18n.WorkPackageListPage.View.Sort.byName',
        id: 'name',
        icon: 'sort-alphabet',
      },
    ];
  }
}
